// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui'

interface Props {
  children:  ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?:   Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('WilsApp ErrorBoundary caught:', error, info)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex flex-col items-center justify-center p-8 text-center rounded-2xl bg-status-alertBg border border-status-alertBorder">
          <AlertCircle className="h-10 w-10 text-status-alertText mb-3" />
          <h3 className="text-base font-semibold text-ink mb-1">
            Er is iets misgegaan
          </h3>
          <p className="text-sm text-ink-3 mb-5">
            Deze pagina kon niet worden geladen. Probeer het opnieuw.
          </p>
          <Button
            onClick={this.handleReset}
            variant="outline"
            size="sm"
            icon={<RefreshCw className="h-3.5 w-3.5" />}
          >
            Opnieuw proberen
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}
